import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import {
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	Plus,
	Search,
	Upload,
} from "lucide-react";
import { getClients, getUsers } from "../config/url";
import { getConfigWithToken } from "../config/config";
import AddClientDrawer from "../components/AddClientDrawer";
import classes from "./Clients.module.css";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const SKELETON_ROWS = 8;

const formatDateUS = (value) => {
	if (!value) return "—";
	const d = new Date(value.includes("T") ? value : `${value}T00:00:00`);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toLocaleDateString("en-US", {
		month: "numeric",
		day: "numeric",
		year: "numeric",
	});
};

const formatPhone = (value) => {
	if (!value) return "—";
	const digits = String(value).replace(/\D/g, "");
	if (digits.length === 10) {
		return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
	}
	return value;
};

const normalizeStatus = (status) => {
	if (status == null || status === "") return "unassigned";
	if (typeof status === "boolean") return status ? "active" : "inactive";
	if (typeof status === "number") return status === 1 ? "active" : "inactive";
	const normalized = String(status).trim().toLowerCase();
	if (["active", "true", "1"].includes(normalized)) return "active";
	if (["inactive", "false", "0"].includes(normalized)) return "inactive";
	if (normalized === "unassigned") return "unassigned";
	return "unassigned";
};

const statusLabel = (status) => {
	const key = normalizeStatus(status);
	if (key === "active") return "Active";
	if (key === "inactive") return "Inactive";
	return "Unassigned";
};

const statusClass = (status) => {
	const key = normalizeStatus(status);
	if (key === "active") return classes.statusActive;
	if (key === "inactive") return classes.statusInactive;
	return classes.statusUnassigned;
};

const formatAssignedProvider = (value) => {
	if (!value || String(value).trim() === "" || String(value).trim() === "—") {
		return "Unassigned";
	}
	return value;
};

const Clients = () => {
	const currentUser = useSelector((state) => state?.user);
	const token = currentUser?.user?.token || localStorage.getItem("user_id");
	const organizationId =
		currentUser?.user?.organizationId ||
		localStorage.getItem("organizationId") ||
		localStorage.getItem("businessId") ||
		"";
	const navigate = useNavigate();
	const location = useLocation();

	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState("");
	const [providerFilter, setProviderFilter] = useState("");
	const [clients, setClients] = useState([]);
	const [providers, setProviders] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(25);
	const [totalPages, setTotalPages] = useState(1);
	const [addDrawerOpen, setAddDrawerOpen] = useState(false);

	useEffect(() => {
		if (location.state?.openAddClient) {
			setAddDrawerOpen(true);
			navigate(location.pathname, { replace: true, state: {} });
		}
	}, [location.pathname, location.state, navigate]);

	const requestConfig = useMemo(
		() => ({
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
				...(token ? getConfigWithToken(token).headers : {}),
				...(organizationId ? { "x-organization-id": organizationId } : {}),
			},
		}),
		[organizationId, token],
	);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
		return () => clearTimeout(timer);
	}, [search]);

	useEffect(() => {
		setCurrentPage(1);
	}, [debouncedSearch, statusFilter, providerFilter, rowsPerPage]);

	useEffect(() => {
		const loadProviders = async () => {
			try {
				const response = await axios.get(getUsers, requestConfig);
				const rows = response?.data?.data || [];
				const list = (Array.isArray(rows) ? rows : [])
					.map((u) => ({
						id: u._id,
						name:
							[u.firstname, u.lastname].filter(Boolean).join(" ").trim() ||
							u.userId ||
							u.email ||
							"Provider",
					}))
					.filter((p) => p.id)
					.sort((a, b) => a.name.localeCompare(b.name));
				setProviders(list);
			} catch {
				setProviders([]);
			}
		};
		loadProviders();
	}, [requestConfig]);

	const loadClients = useCallback(async () => {
		if (!organizationId) {
			setClients([]);
			setTotalPages(1);
			setLoading(false);
			setError("Organization context is required.");
			return;
		}
		setLoading(true);
		setError(null);
		try {
			const response = await axios.get(getClients, {
				...requestConfig,
				params: {
					limit: rowsPerPage,
					organizationId,
					page: currentPage,
					...(debouncedSearch ? { search: debouncedSearch } : {}),
					...(statusFilter ? { status: statusFilter } : {}),
					...(providerFilter ? { providerId: providerFilter } : {}),
				},
			});

			const rows = response?.data?.data || [];
			const pagination = response?.data?.pagination || {};
			setClients(Array.isArray(rows) ? rows : []);
			setTotalPages(pagination.totalPages || 1);
		} catch (err) {
			setClients([]);
			setTotalPages(1);
			setError(
				err?.response?.data?.message ||
					err?.message ||
					"Unable to load clients. Please try again.",
			);
		} finally {
			setLoading(false);
		}
	}, [
		currentPage,
		debouncedSearch,
		organizationId,
		providerFilter,
		requestConfig,
		rowsPerPage,
		statusFilter,
	]);

	useEffect(() => {
		loadClients();
	}, [loadClients]);

	const openClient = (client) => {
		if (!client?.id) return;
		navigate(`/clients/${client.id}`);
	};

	const handleRowKeyDown = (event, client) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			openClient(client);
		}
	};

	const renderTableBody = () => {
		if (loading) {
			return Array.from({ length: SKELETON_ROWS }, (_, index) => (
				<tr aria-hidden="true" className={classes.skeletonRow} key={`skeleton-${index}`}>
					{Array.from({ length: 7 }).map((__, cellIndex) => (
						<td key={cellIndex}>
							<div className={classes.skeletonCell} />
						</td>
					))}
				</tr>
			));
		}

		if (clients.length === 0) {
			return (
				<tr>
					<td className={classes.emptyCell} colSpan={7}>
						<p className={classes.emptyTitle}>No clients found</p>
						<p className={classes.stateText}>
							{debouncedSearch || statusFilter || providerFilter
								? "Try adjusting your search or filters."
								: "Add a client to get started."}
						</p>
					</td>
				</tr>
			);
		}

		return clients.map((row) => {
			const name = [row.firstName, row.lastName].filter(Boolean).join(" ") || "—";
			return (
				<tr
					className={classes.row}
					key={row.id || row.clientid}
					onClick={() => openClient(row)}
					onKeyDown={(e) => handleRowKeyDown(e, row)}
					role="link"
					tabIndex={0}>
					<td className={classes.clientName}>{name}</td>
					<td>{formatDateUS(row.dob)}</td>
					<td className={classes.clientId}>{row.clientid || "—"}</td>
					<td>{formatPhone(row.phone_number)}</td>
					<td>{formatAssignedProvider(row.assignedUser)}</td>
					<td>{formatDateUS(row.lastVisit)}</td>
					<td>
						<span className={`${classes.statusPill} ${statusClass(row.status)}`}>
							{statusLabel(row.status)}
						</span>
					</td>
				</tr>
			);
		});
	};

	return (
		<div className={classes.page}>
			<div className={classes.pageHeader}>
				<h1 className={classes.title}>Clients</h1>
			</div>

			<div className={classes.toolbar}>
				<div className={classes.filters}>
					<div className={classes.searchWrap}>
						<Search aria-hidden="true" className={classes.searchIcon} size={15} strokeWidth={2} />
						<input
							aria-label="Search clients"
							className={classes.searchInput}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Search by name, ID, or phone..."
							type="search"
							value={search}
						/>
					</div>

					<div className={classes.selectWrap}>
						<select
							aria-label="Filter by status"
							className={classes.filterSelect}
							onChange={(e) => setStatusFilter(e.target.value)}
							value={statusFilter}>
							<option value="">All Statuses</option>
							<option value="Active">Active</option>
							<option value="Inactive">Inactive</option>
							<option value="Unassigned">Unassigned</option>
						</select>
						<ChevronDown aria-hidden="true" className={classes.selectIcon} size={15} strokeWidth={2} />
					</div>

					<div className={classes.selectWrap}>
						<select
							aria-label="Filter by provider"
							className={classes.filterSelect}
							onChange={(e) => setProviderFilter(e.target.value)}
							value={providerFilter}>
							<option value="">All Providers</option>
							<option value="unassigned">Unassigned</option>
							{providers.map((provider) => (
								<option key={provider.id} value={provider.id}>
									{provider.name}
								</option>
							))}
						</select>
						<ChevronDown aria-hidden="true" className={classes.selectIcon} size={15} strokeWidth={2} />
					</div>
				</div>

				<div className={classes.actions}>
					<button
						className={classes.importButton}
						onClick={() => toast.info("Client import will be available soon.")}
						type="button">
						<Upload size={15} strokeWidth={2} />
						Import
					</button>

					<button className={classes.addButton} onClick={() => setAddDrawerOpen(true)} type="button">
						<Plus size={15} strokeWidth={2.5} />
						Add Client
					</button>
				</div>
			</div>

			<div className={classes.card}>
				{error ? (
					<div className={classes.errorState} role="alert">
						<p className={classes.errorTitle}>Couldn&apos;t load clients</p>
						<p className={classes.stateText}>{error}</p>
						<button className={classes.retryButton} onClick={loadClients} type="button">
							Try Again
						</button>
					</div>
				) : (
					<>
						<div className={classes.tableWrap}>
							<table aria-busy={loading} className={classes.table}>
								<thead>
									<tr>
										<th>Client Name</th>
										<th>Date of Birth</th>
										<th>Client ID</th>
										<th>Phone</th>
										<th>Assigned Provider</th>
										<th>Last Visit</th>
										<th>Status</th>
									</tr>
								</thead>
								<tbody>{renderTableBody()}</tbody>
							</table>
						</div>

						<div className={classes.pagination}>
							<label className={classes.rowsPerPage}>
								<span>Rows per page</span>
								<div className={classes.rowsSelectWrap}>
									<select
										aria-label="Rows per page"
										className={classes.rowsSelect}
										onChange={(e) => setRowsPerPage(Number(e.target.value))}
										value={rowsPerPage}>
										{ROWS_PER_PAGE_OPTIONS.map((option) => (
											<option key={option} value={option}>
												{option}
											</option>
										))}
									</select>
									<ChevronDown aria-hidden="true" className={classes.rowsSelectIcon} size={14} />
								</div>
							</label>

							<div className={classes.pageControls}>
								<button
									aria-label="Previous page"
									className={classes.pageButton}
									disabled={loading || currentPage <= 1}
									onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
									type="button">
									<ChevronLeft size={18} strokeWidth={2} />
								</button>
								<span className={classes.pageLabel}>
									Page {currentPage} of {totalPages}
								</span>
								<button
									aria-label="Next page"
									className={classes.pageButton}
									disabled={loading || currentPage >= totalPages}
									onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
									type="button">
									<ChevronRight size={18} strokeWidth={2} />
								</button>
							</div>
						</div>
					</>
				)}
			</div>

			<AddClientDrawer
				onClose={() => setAddDrawerOpen(false)}
				onCreated={() => {
					setCurrentPage(1);
					loadClients();
				}}
				open={addDrawerOpen}
			/>
		</div>
	);
};

export default Clients;
